import * as React from 'react'

import { gdprBannerContent } from './gdpr-banner-content'

/** Loads Iubenda widget overrides; mount only when the Iubenda script is rendered. */
export function IubendaStyles() {
  React.useEffect(() => {
    void import('./iubenda-styles')
  }, [])

  return null
}

export type IubendaVariant = 'locationNotSet' | 'strictMode' | 'laxMode' | 'notApplicable'

interface IubendaScriptProps {
  type?: 'text/plain'
  className?: string
  'data-cf-defer'?: true
  'data-iub-purposes'?: string
}

interface IubendaUsPrivacyPreferences {
  adv?: boolean
  s?: boolean
  sh?: boolean
}

interface IubendaPreference {
  uspr?: IubendaUsPrivacyPreferences
  purposes?: Record<string, boolean>
}

interface IubendaApi {
  activateSnippets?: (options?: { runOnActivationDoneCallback: boolean }) => void
  getPreferences?: () => IubendaPreference | null
  getPurposesState?: () => (Record<string, boolean> & IubendaUsPrivacyPreferences) | null
  isPreferenceExpressed?: () => boolean
  openPreferences?: () => void
}

interface IubendaBridge {
  usprApplies?: boolean
  usprRequiresConsent?: boolean
  gdprApplies?: boolean
  failed?: boolean
  withdrawing?: boolean
  activateSnippets?: (preference?: IubendaPreference | null) => void
  getApi?: () => IubendaApi | undefined
  laxTrackingAllowed?: boolean
  openPreferences?: () => boolean
  openPreferencesWhenReady?: () => boolean
  ready?: boolean
}

interface IubendaWindowGlobals {
  CF_IUBENDA?: IubendaBridge
  _iub?: {
    cs?: {
      api?: IubendaApi
    }
    csConfiguration?: {
      callback?: Record<string, (...args: unknown[]) => void>
    }
  }
}

interface CreateIubendaBridgeScriptOptions {
  usprApplies?: boolean
  gdprApplies?: boolean
  autoOpenPreferences?: boolean
}

interface UseIubendaOptions {
  enableGdpr?: boolean
  country?: string
  enabled?: boolean
  locationSet?: boolean
  region?: string
}

export interface IubendaConsentEventDetail {
  apiAvailable: boolean
  preference: IubendaPreference | null
  preferenceExpressed: boolean
  trackingAllowed: boolean
  type: string
}

export interface IubendaConsentState extends IubendaConsentEventDetail {
  revision: number
}

export const iubendaConsentEventName = 'cf:iubenda-consent'

export const IUBENDA_FEATURE_FLAG = 'iubenda-us-privacy-enabled'

export const iubendaSrc =
  'https://embeds.iubenda.com/widgets/86965d08-6db6-11ee-8bfc-5ad8d8c564c0.js'

const iubendaPreferenceScript = `function cfGetApi() {
    return window._iub && window._iub.cs && window._iub.cs.api;
}

function cfGetPreference(preference) {
    if (preference && typeof preference === 'object') {
        return preference;
    }

    var api = cfGetApi();
    if (api && typeof api.getPreferences === 'function') {
        var prefs = api.getPreferences();
        if (prefs) {
            return prefs;
        }
    }

    return null;
}

function cfIsPreferenceExpressed() {
    var api = cfGetApi();
    return !!(api && typeof api.isPreferenceExpressed === 'function' && api.isPreferenceExpressed());
}

function cfUsPurposes(preference) {
    var api = cfGetApi();
    var saved = api && typeof api.getPreferences === 'function' && api.getPreferences();
    if (saved && saved.uspr) return saved.uspr;
    var state = api && typeof api.getPurposesState === 'function' && api.getPurposesState();
    return state || (cfGetPreference(preference) || {}).uspr;
}

function cfUsTrackingAllowed(preference, purposes) {
    var uspr = cfUsPurposes(preference);
    var required = purposes && !/^[0-9, ]+$/.test(purposes) ? purposes.split(',') : ['s', 'sh', 'adv'];
    return !!uspr && required.every(function (purpose) { return uspr[purpose.trim()] === true; });
}

function cfDeferTrackingAllowed(preference, purpose) {
    if (bridge.failed || bridge.withdrawing) {
        return false;
    }
    if (bridge.usprApplies) {
        return (cfIsPreferenceExpressed() || (!bridge.usprRequiresConsent && bridge.laxTrackingAllowed)) &&
            cfUsTrackingAllowed(preference, purpose);
    }
    if (!cfIsPreferenceExpressed()) {
        return false;
    }

    var normalizedPreference = cfGetPreference(preference);
    if (bridge.gdprApplies) {
        return !!(normalizedPreference && normalizedPreference.purposes && normalizedPreference.purposes[purpose || '4'] === true);
    }
    var uspr = normalizedPreference && normalizedPreference.uspr;

    if (uspr) {
        return !!(uspr.s || uspr.sh || uspr.adv);
    }

    return false;
}

function cfDispatchIubendaEvent(type, preference) {
    if (typeof window.CustomEvent !== 'function') {
        return;
    }

    var normalizedPreference = cfGetPreference(preference);
    var api = cfGetApi();

    window.dispatchEvent(new CustomEvent(consentEventName, {
        detail: {
            apiAvailable: !!api,
            preference: normalizedPreference,
            preferenceExpressed: cfIsPreferenceExpressed(),
            trackingAllowed: cfDeferTrackingAllowed(normalizedPreference),
            type: type
        }
    }));
}

function cfUpdateLaxTrackingAllowed() {
    cfLaxTrackingAllowed = !cfIsPreferenceExpressed() && (!bridge.usprApplies || cfUsTrackingAllowed());
    bridge.laxTrackingAllowed = cfLaxTrackingAllowed;
}

function cfVendorConsent(preference) {
    var measurementAllowed = cfDeferTrackingAllowed(preference, '4');
    var marketingAllowed = cfDeferTrackingAllowed(preference, '5');
    var consent = {
        ad_storage: marketingAllowed ? 'granted' : 'denied',
        ad_user_data: marketingAllowed ? 'granted' : 'denied',
        ad_personalization: marketingAllowed ? 'granted' : 'denied',
        analytics_storage: measurementAllowed ? 'granted' : 'denied'
    };

    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', consent);
    }
    if (window.uetq && typeof window.uetq.push === 'function') {
        window.uetq.push('consent', 'update', { ad_storage: consent.ad_storage });
    }
    if (typeof window.fbq === 'function') {
        window.fbq('consent', marketingAllowed ? 'grant' : 'revoke');
    }
}

function cfInitializeVendorConsent() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500
    });

    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'default', { ad_storage: 'denied' });

    if (!window.fbq) {
        var fbq = function () {
            if (fbq.callMethod) {
                fbq.callMethod.apply(fbq, arguments);
            } else {
                fbq.queue.push(arguments);
            }
        };
        fbq.queue = [];
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        window.fbq = fbq;
        window._fbq = fbq;
    }
    window.fbq('consent', 'revoke');
}

`

const iubendaActivationScript = `function cfActivateDeferredScripts(preference) {
    document.querySelectorAll('script[data-cf-defer]:not([data-cf-defer-active])').forEach(function (el) {
        if (!cfDeferTrackingAllowed(preference, el.getAttribute('data-iub-purposes') || '5')) {
            return;
        }
        el.setAttribute('data-cf-defer-active', '1');
        var s = document.createElement('script');
        var src = el.getAttribute('data-cf-src') || el.getAttribute('src');
        if (src) {
            s.src = src;
        } else {
            s.text = el.textContent;
        }
        if (el.hasAttribute('async')) {
            s.async = true;
        }
        if (el.hasAttribute('defer')) {
            s.defer = true;
        }
        el.parentNode.insertBefore(s, el);
    });
}

function cfActivateSnippets(preference) {
    var api = cfGetApi();
    if (api && typeof api.activateSnippets === 'function') {
        api.activateSnippets({ runOnActivationDoneCallback: true });
    }

    if (!bridge.gdprApplies) {
        cfActivateDeferredScripts(preference);
    }
}

`

const iubendaDialogScript = `function cfOpenPreferences() {
    var api = cfGetApi();
    if (!api || typeof api.openPreferences !== 'function') {
        return false;
    }

    cfPreferencesOpened = true;
    api.openPreferences();
    cfDispatchIubendaEvent('preferences-opened');
    return true;
}

function cfCanAutoOpenPreferences() {
    var api = cfGetApi();
    if (!autoOpenPreferences || cfPreferencesOpened) {
        return false;
    }

    if (!api) {
        return true;
    }

    if (typeof api.isPreferenceExpressed === 'function' && api.isPreferenceExpressed()) {
        return false;
    }

    return true;
}

function cfOpenPreferencesWhenReady(attemptsLeft, requireUnexpressedPreference) {
    if (requireUnexpressedPreference && !cfCanAutoOpenPreferences()) {
        return;
    }

    if (cfOpenPreferences()) {
        return;
    }

    if (attemptsLeft > 0) {
        window.setTimeout(function () {
            cfOpenPreferencesWhenReady(attemptsLeft - 1, requireUnexpressedPreference);
        }, 250);
    }
}

function cfAutoOpenPreferencesWhenReady() {
    if (!autoOpenPreferences || cfPreferencesOpened) {
        return;
    }

    window.setTimeout(function () {
        cfOpenPreferencesWhenReady(40, true);
    }, 0);
}

`

const iubendaCallbackScript = `function cfRevokeTracking() {
    if (bridge.withdrawing) return;
    bridge.withdrawing = true;
    if (bridge.gdprApplies) cfVendorConsent(null);
    cfDispatchIubendaEvent('withdrawal');
    window.setTimeout(function () { window.location.reload(); }, 0);
}

function cfSyncUsPreferences() {
    if (!bridge.usprApplies) return;
    if (bridge.usprRequiresConsent && !cfIsPreferenceExpressed()) return;
    var current = cfUsPurposes();
    if (!current) return;
    var revoked = cfUsLastPurposes && ['s', 'sh', 'adv'].some(function (purpose) {
        return cfUsLastPurposes[purpose] === true && current[purpose] !== true;
    });
    cfUsLastPurposes = Object.assign({}, current);
    if (revoked) cfRevokeTracking();
}

bridge.getApi = cfGetApi;
bridge.openPreferences = function () {
    return cfOpenPreferences();
};
bridge.openPreferencesWhenReady = function () {
    cfOpenPreferencesWhenReady(40, false);
    return true;
};
bridge.activateSnippets = cfActivateSnippets;
bridge.ready = true;

(function (existingOnReady) {
    window._iub.csConfiguration.callback.onReady = function (preference) {
        if (bridge.gdprApplies) cfVendorConsent(preference);
        if (typeof existingOnReady === 'function') {
            existingOnReady.apply(this, arguments);
        }
        cfSyncUsPreferences();
        if (!bridge.gdprApplies) cfActivateSnippets();
        cfDispatchIubendaEvent('ready');
        cfAutoOpenPreferencesWhenReady();
    };
}(window._iub.csConfiguration.callback.onReady));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onPreferenceExpressed = function (preference) {
        cfSyncUsPreferences();
        if (bridge.gdprApplies) cfVendorConsent(preference);
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        if (!bridge.gdprApplies) {
            cfActivateSnippets(preference);
        }
        cfDispatchIubendaEvent('preference-expressed', preference);
    };
}(window._iub.csConfiguration.callback.onPreferenceExpressed));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onPreferenceExpressedOrNotNeeded = function (preference) {
        cfSyncUsPreferences();
        if (bridge.gdprApplies) cfVendorConsent(preference);
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        cfUpdateLaxTrackingAllowed();
        if (!bridge.gdprApplies) {
            cfActivateSnippets(preference);
        }
        cfDispatchIubendaEvent('preference-expressed-or-not-needed', preference);
    };
}(window._iub.csConfiguration.callback.onPreferenceExpressedOrNotNeeded));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onActivationDone = function (preference) {
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
        cfDispatchIubendaEvent('activation-done', preference);
    };
}(window._iub.csConfiguration.callback.onActivationDone));

(function (existingCallback) {
    window._iub.csConfiguration.callback.onPreferenceChange = function (change) {
        var previous = change && change.previousPreferences;
        var current = change && change.currentPreferences;
        var revoked = bridge.gdprApplies && previous && current && previous.purposes && current.purposes &&
            Object.keys(previous.purposes).some(function (purpose) {
                return purpose !== '1' && previous.purposes[purpose] === true && current.purposes[purpose] !== true;
            });
        if (revoked) cfRevokeTracking();
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
    };
}(window._iub.csConfiguration.callback.onPreferenceChange));

['onStartupFailed', 'onFatalError'].forEach(function (name) {
    var existingCallback = window._iub.csConfiguration.callback[name];
    window._iub.csConfiguration.callback[name] = function () {
        bridge.failed = true;
        if (bridge.gdprApplies) cfVendorConsent(null);
        cfDispatchIubendaEvent('failed');
        if (typeof existingCallback === 'function') {
            existingCallback.apply(this, arguments);
        }
    };
});

`

export const createIubendaBridgeScript = ({
  autoOpenPreferences = false,
  gdprApplies = false,
  usprApplies = false,
}: CreateIubendaBridgeScriptOptions = {}) => `
(function () {
window._iub = window._iub || {};
window._iub.csConfiguration = window._iub.csConfiguration || {};
window._iub.csConfiguration.callback = window._iub.csConfiguration.callback || {};

if (window.location.hostname.endsWith('.vercel.app')) {
    window._iub.csConfiguration.localConsentDomainExact = true;
}


var bridge = window.CF_IUBENDA = window.CF_IUBENDA || {};
bridge.gdprApplies = ${gdprApplies ? 'true' : 'false'};
bridge.usprApplies = ${usprApplies ? 'true' : 'false'};
bridge.usprRequiresConsent = ${autoOpenPreferences ? 'true' : 'false'};
${
  gdprApplies
    ? `var bannerContent = ${JSON.stringify(gdprBannerContent)};
var bannerLanguage = (document.documentElement.lang || 'en').toLowerCase().split(/[-_]/)[0];
if (!Object.prototype.hasOwnProperty.call(bannerContent, bannerLanguage)) {
    bannerLanguage = 'en';
}
Object.assign(window._iub.csConfiguration, {
    lang: bannerLanguage,
    enableGdpr: true,
    googleConsentMode: false,
    gdprApplies: true,
    gdprAppliesGlobally: false,
    usprApplies: false,
    uetConsentMode: false,
    perPurposeConsent: true,
    banner: Object.assign({}, window._iub.csConfiguration.banner, bannerContent[bannerLanguage], {
        acceptButtonDisplay: true,
        rejectButtonDisplay: true,
        customizeButtonDisplay: true,
        closeButtonDisplay: false,
        closeButtonRejects: true
    })
});`
    : ''
}
var consentEventName = '${iubendaConsentEventName}';
var autoOpenPreferences = ${autoOpenPreferences ? 'true' : 'false'};
var cfPreferencesOpened = false;
var cfUsLastPurposes;
var cfLaxTrackingAllowed = !!bridge.laxTrackingAllowed;

${iubendaPreferenceScript}${gdprApplies ? 'cfInitializeVendorConsent();' : ''}${iubendaActivationScript}${iubendaDialogScript}${iubendaCallbackScript}document.addEventListener('DOMContentLoaded', function () {
    cfActivateSnippets();
});

cfDispatchIubendaEvent('bridge-ready');
}());
`

export const californiaConsentScript = createIubendaBridgeScript({ autoOpenPreferences: true })

const initialIubendaConsentState: IubendaConsentState = {
  apiAvailable: false,
  preference: null,
  preferenceExpressed: false,
  revision: 0,
  trackingAllowed: false,
  type: 'initial',
}

const getIubendaWindow = () => window as Window & typeof globalThis & IubendaWindowGlobals

export function getIubendaApi() {
  if (typeof window === 'undefined') {
    return undefined
  }

  return getIubendaWindow()._iub?.cs?.api
}

const getIubendaBridge = () => {
  if (typeof window === 'undefined') {
    return undefined
  }

  return getIubendaWindow().CF_IUBENDA
}

function isIubendaUnavailable() {
  const bridge = getIubendaBridge()
  return Boolean(bridge?.failed || bridge?.withdrawing)
}

export function setIubendaGdprApplies(applies: boolean) {
  if (typeof window === 'undefined') {
    return
  }
  const globals = getIubendaWindow()
  globals.CF_IUBENDA ??= {}
  globals.CF_IUBENDA.gdprApplies = applies
}

export function setIubendaUsPrivacyApplies(applies: boolean, requiresConsent: boolean) {
  if (typeof window === 'undefined') {
    return
  }
  const globals = getIubendaWindow()
  globals.CF_IUBENDA ??= {}
  globals.CF_IUBENDA.usprApplies = applies
  globals.CF_IUBENDA.usprRequiresConsent = requiresConsent
}

export function isIubendaPurposeAllowed(purpose = '4') {
  if (getIubendaBridge()?.usprApplies) {
    return isIubendaTrackingAllowed()
  }
  if (!getIubendaBridge()?.gdprApplies) {
    return true
  }
  if (isIubendaUnavailable()) {
    return false
  }
  try {
    const api = getIubendaApi()
    return (
      api?.isPreferenceExpressed?.() === true &&
      api.getPreferences?.()?.purposes?.[purpose] === true
    )
  } catch {
    return false
  }
}

export const isIubendaBridgeReady = (requiresGdpr = false) => {
  const bridge = getIubendaBridge()
  return Boolean(bridge?.ready && (!requiresGdpr || bridge.gdprApplies))
}

const getIubendaPreference = (preference?: IubendaPreference | null) => {
  if (preference) {
    return preference
  }

  return getIubendaApi()?.getPreferences?.() ?? null
}

const isIubendaPreferenceExpressed = () => {
  return getIubendaApi()?.isPreferenceExpressed?.() ?? false
}

const usPrivacyPurposes = ['s', 'sh', 'adv'] as const

function getUsPrivacyPurposes(preference?: IubendaPreference | null) {
  const api = getIubendaApi()
  return api?.getPreferences?.()?.uspr ?? api?.getPurposesState?.() ?? preference?.uspr
}

function isUsPrivacyTrackingAllowed(preference?: IubendaPreference | null) {
  if (isIubendaUnavailable()) {
    return false
  }
  const bridge = getIubendaBridge()
  const mayActivate =
    isIubendaPreferenceExpressed() || (!bridge?.usprRequiresConsent && bridge?.laxTrackingAllowed)
  if (!mayActivate) {
    return false
  }
  const uspr = getUsPrivacyPurposes(preference)
  return usPrivacyPurposes.every(purpose => uspr?.[purpose] === true)
}

export const isIubendaTrackingAllowed = (preference?: IubendaPreference | null) => {
  if (getIubendaBridge()?.usprApplies) {
    return isUsPrivacyTrackingAllowed(preference)
  }
  if (isIubendaUnavailable() || !isIubendaPreferenceExpressed()) {
    return false
  }

  const normalizedPreference = getIubendaPreference(preference)
  if (getIubendaBridge()?.gdprApplies) {
    return normalizedPreference?.purposes?.['4'] === true
  }
  const uspr = normalizedPreference?.uspr

  return Boolean(uspr?.s || uspr?.sh || uspr?.adv)
}

const readIubendaConsentState = (
  detail?: Partial<IubendaConsentEventDetail>,
  revision = 0,
): IubendaConsentState => {
  if (typeof window === 'undefined') {
    return initialIubendaConsentState
  }

  const preference = detail?.preference ?? getIubendaPreference()

  return {
    apiAvailable: detail?.apiAvailable ?? Boolean(getIubendaApi()),
    preference,
    preferenceExpressed: detail?.preferenceExpressed ?? isIubendaPreferenceExpressed(),
    revision,
    trackingAllowed: detail?.trackingAllowed ?? isIubendaTrackingAllowed(preference),
    type: detail?.type ?? 'snapshot',
  }
}

export function useIubendaConsent() {
  const [state, setState] = React.useState<IubendaConsentState>(() => readIubendaConsentState())

  React.useEffect(() => {
    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<Partial<IubendaConsentEventDetail>>).detail

      setState(currentState => readIubendaConsentState(detail, currentState.revision + 1))
    }

    window.addEventListener(iubendaConsentEventName, handleConsentEvent)
    setState(currentState => readIubendaConsentState(undefined, currentState.revision))

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleConsentEvent)
    }
  }, [])

  const openPreferences = React.useCallback(() => {
    if (getIubendaBridge()?.openPreferencesWhenReady?.()) {
      return
    }

    if (getIubendaBridge()?.openPreferences?.()) {
      return
    }

    getIubendaApi()?.openPreferences?.()
  }, [])

  const activateSnippets = React.useCallback((preference?: IubendaPreference | null) => {
    const bridge = getIubendaBridge()
    if (bridge?.activateSnippets) {
      bridge.activateSnippets(preference)
      return
    }

    getIubendaApi()?.activateSnippets?.()
  }, [])

  return React.useMemo(
    () => ({
      ...state,
      activateSnippets,
      openPreferences,
    }),
    [activateSnippets, openPreferences, state],
  )
}

function useIubendaLaxActivation(variant: IubendaVariant) {
  const [laxActivationAllowed, setLaxActivationAllowed] = React.useState(
    () => getIubendaBridge()?.laxTrackingAllowed ?? false,
  )

  React.useEffect(() => {
    if (variant !== 'laxMode') {
      setLaxActivationAllowed(false)
      return
    }

    setLaxActivationAllowed(getIubendaBridge()?.laxTrackingAllowed ?? false)

    const handleConsentEvent = (event: Event) => {
      const detail = (event as CustomEvent<Partial<IubendaConsentEventDetail>>).detail
      if (detail?.preferenceExpressed) {
        setLaxActivationAllowed(false)
        return
      }
      if (detail?.apiAvailable && detail.type === 'preference-expressed-or-not-needed') {
        const bridge = getIubendaBridge()
        if (bridge) {
          bridge.laxTrackingAllowed = true
        }
        setLaxActivationAllowed(true)
      }
    }

    window.addEventListener(iubendaConsentEventName, handleConsentEvent)
    return () => {
      window.removeEventListener(iubendaConsentEventName, handleConsentEvent)
    }
  }, [variant])

  return laxActivationAllowed
}

function readUsPrivacyTrackingState(
  variant: IubendaVariant,
  preference: IubendaPreference | null,
  expressed: boolean,
) {
  const uspr = getUsPrivacyPurposes(preference)
  if (usPrivacyPurposes.some(key => uspr?.[key] === false)) {
    return false
  }
  if (expressed) {
    return isUsPrivacyTrackingAllowed(preference)
  }
  if (variant === 'strictMode') {
    return 'pending'
  }
  if (!getIubendaBridge()?.laxTrackingAllowed) {
    return 'pending'
  }
  return isUsPrivacyTrackingAllowed(preference)
}

export function useIubendaTrackingState(
  variant: IubendaVariant,
  purpose = '4',
): boolean | 'pending' {
  const { apiAvailable, preference, preferenceExpressed, trackingAllowed, type } =
    useIubendaConsent()
  const laxActivationAllowed = useIubendaLaxActivation(variant)

  if (variant === 'notApplicable') {
    return true
  }

  if (variant === 'locationNotSet') {
    return 'pending'
  }

  if (isIubendaUnavailable()) {
    return false
  }

  if (getIubendaBridge()?.usprApplies) {
    return readUsPrivacyTrackingState(variant, preference, preferenceExpressed)
  }

  if (preferenceExpressed) {
    if (getIubendaBridge()?.gdprApplies) {
      return preference?.purposes?.[purpose] === true
    }
    return trackingAllowed
  }

  if (variant === 'strictMode') {
    return 'pending'
  }

  if (laxActivationAllowed || (apiAvailable && type === 'preference-expressed-or-not-needed')) {
    return true
  }

  return 'pending'
}

export function useIubendaTrackingAllowed(variant: IubendaVariant, purpose = '4') {
  return useIubendaTrackingState(variant, purpose) === true
}

export function useIubendaSnippetActivation(enabled = true) {
  React.useEffect(() => {
    if (!enabled) {
      return
    }

    const bridge = getIubendaBridge()
    if (bridge?.activateSnippets) {
      bridge.activateSnippets()
      return
    }

    getIubendaApi()?.activateSnippets?.()
  }, [enabled])
}

const gdprCountries = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
])

function isGdprCountry(country: string) {
  return gdprCountries.has(country) || ['', 'XX', 'T1', 'UNKNOWN'].includes(country)
}

function getIubendaLocationVariant(country?: string, region?: string): IubendaVariant {
  const normalizedCountry = country?.trim().toUpperCase() ?? ''
  const normalizedRegion = region?.trim().toLowerCase() ?? ''
  const isUncertainCountry = ['', 'XX', 'T1'].includes(normalizedCountry)
  if (isUncertainCountry) {
    return 'strictMode'
  }
  if (normalizedCountry !== 'US') {
    return 'notApplicable'
  }
  const requiresConsent = ['', 'unknown', 'xx', 't1', 'california', 'ca'].includes(normalizedRegion)
  return requiresConsent ? 'strictMode' : 'laxMode'
}

const iubendaScriptProps: Record<IubendaVariant, IubendaScriptProps> = {
  notApplicable: {},
  locationNotSet: {},
  strictMode: { type: 'text/plain', 'data-cf-defer': true },
  laxMode: {
    type: 'text/plain',
    className: '_iub_cs_activate',
    'data-iub-purposes': 's,sh,adv',
  },
}

function resolveIubendaVariant(
  { enabled, enableGdpr, locationSet, country, region }: UseIubendaOptions,
  gdprApplies: boolean,
): IubendaVariant {
  if (!enabled && !enableGdpr) {
    return 'notApplicable'
  }
  if (!locationSet) {
    return 'locationNotSet'
  }
  if (gdprApplies) {
    return 'strictMode'
  }
  return enabled ? getIubendaLocationVariant(country, region) : 'notApplicable'
}

export function useIubenda({
  country,
  enableGdpr = false,
  enabled = false,
  locationSet = false,
  region,
}: UseIubendaOptions = {}): {
  scriptProps: IubendaScriptProps
  variant: IubendaVariant
  gdprApplies?: boolean
  usprApplies?: boolean
} {
  const normalizedCountry = country?.trim().toUpperCase() ?? ''
  const gdprApplies = enableGdpr && locationSet && isGdprCountry(normalizedCountry)
  const variant = resolveIubendaVariant(
    { enabled, enableGdpr, locationSet, country, region },
    gdprApplies,
  )
  const scriptProps: IubendaScriptProps = gdprApplies
    ? { type: 'text/plain', className: '_iub_cs_activate', 'data-iub-purposes': '5' }
    : iubendaScriptProps[variant]
  const usprApplies = enableGdpr && enabled && locationSet && normalizedCountry === 'US'
  if (usprApplies) {
    return {
      scriptProps: { type: 'text/plain', 'data-cf-defer': true, 'data-iub-purposes': 's,sh,adv' },
      variant,
      gdprApplies,
      usprApplies,
    }
  }
  return { scriptProps, variant, gdprApplies, usprApplies }
}
